import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import './styles/FHSlider.scss';

/**
 * Free horizontal slider with navigation controls.
 * Exposes the inner viewport via forwarded ref so parents can control scrolling.
 */
const FHSlider = forwardRef(function FHSlider(
  {
    children,
    className = "",
    customPrevButton = null,
    customNextButton = null,
    onSlideEnd = null,
    disableInternalReset = false, // new: parent can opt out of internal reset
    loop = false,
  },
  forwardedRef // expected to receive the inner viewport DOM node
) {
  const [displayPrev, setDisplayPrev] = useState(false);
  const [displayNext, setDisplayNext] = useState(false);
  const fhSliderRef = useRef(null);
  const onSlideEndRef = useRef(onSlideEnd);
  const slideEndCalledRef = useRef(false);

  useEffect(() => {
    onSlideEndRef.current = onSlideEnd;
  }, [onSlideEnd]);

  // Expose the viewport element to parent via forwardedRef
  useImperativeHandle(forwardedRef, () => fhSliderRef.current, []);

  // Check and update overflow state
  const checkOverflows = () => {
    const el = fhSliderRef.current;
    if (!el) return;
    const { clientWidth, scrollLeft, scrollWidth } = el;
    if (loop && el.children.length > 1) {
      setDisplayPrev(true);
      setDisplayNext(true);
      return;
    }

    setDisplayPrev(scrollLeft > 0);
    setDisplayNext(scrollLeft + clientWidth < scrollWidth);
  };

  // Unified scroll handler
  const handleScroll = () => {
    const el = fhSliderRef.current;
    if (!el) return;

    checkOverflows();

    const { clientWidth, scrollLeft, scrollWidth } = el;
    const offsetThreshold = 250;

    if (
      scrollLeft + clientWidth >= scrollWidth - offsetThreshold &&
      !slideEndCalledRef.current
    ) {
      if (onSlideEndRef.current) {
        onSlideEndRef.current();
        slideEndCalledRef.current = true;
      }
    }
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      checkOverflows();
    });
    if (fhSliderRef.current) {
      resizeObserver.observe(fhSliderRef.current);
    }

    const el = fhSliderRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (el) el.removeEventListener('scroll', handleScroll);
      if (fhSliderRef.current) resizeObserver.disconnect();
    };
  }, [loop]);

  useEffect(() => {
    slideEndCalledRef.current = false;
    checkOverflows();
  }, [children, loop]);

  useEffect(() => {
    if (disableInternalReset || !fhSliderRef.current) return;

    const resetScroll = () => {
      if (!fhSliderRef.current) return;
      fhSliderRef.current.scrollLeft = 0;
      slideEndCalledRef.current = false;
      checkOverflows();
    };

    resetScroll();
    const rafId = requestAnimationFrame(resetScroll);
    const timeoutId = setTimeout(resetScroll, 50);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [disableInternalReset]);

  // Manual navigation helpers (no internal looping/autoplay)
  function slideNext() {
    const el = fhSliderRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const slides = Array.from(el.children);
    if (slides.length === 0) return;

    // find closest
    let closestIdx = 0;
    let minDiff = Infinity;
    slides.forEach((slide, idx) => {
      const diff = Math.abs(slide.offsetLeft - scrollLeft);
      if (diff < minDiff) {
        minDiff = diff;
        closestIdx = idx;
      }
    });

    const nextIdx = closestIdx + 1;
    if (nextIdx >= slides.length) {
      if (loop && slides.length > 0) {
        el.scrollTo({ left: slides[0].offsetLeft, behavior: 'smooth' });
      }
      return;
    }
    el.scrollTo({ left: slides[nextIdx].offsetLeft, behavior: 'smooth' });
  }

  function slidePrev() {
    const el = fhSliderRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const slides = Array.from(el.children);
    if (slides.length === 0) return;

    // find closest
    let closestIdx = 0;
    let minDiff = Infinity;
    slides.forEach((slide, idx) => {
      const diff = Math.abs(slide.offsetLeft - scrollLeft);
      if (diff < minDiff) {
        minDiff = diff;
        closestIdx = idx;
      }
    });

    const prevIdx = closestIdx - 1;
    if (prevIdx < 0) {
      if (loop && slides.length > 0) {
        el.scrollTo({ left: slides[slides.length - 1].offsetLeft, behavior: 'smooth' });
      }
      return;
    }
    el.scrollTo({ left: slides[prevIdx].offsetLeft, behavior: 'smooth' });
  }

  return (
    <div className={`fhslider__container ${className}`} onMouseEnter={checkOverflows}>
      <div
        className="fhslider__viewport"
        ref={fhSliderRef}
        style={{
          scrollBehavior: 'smooth',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </div>

      <div
        className={`fhslider__controls prev-button ${displayPrev ? "active" : "disabled"}`}
        onClick={slidePrev}
      >
        {customPrevButton || "Previous Item"}
      </div>

      <div
        className={`fhslider__controls next-button ${displayNext ? "active" : "disabled"}`}
        onClick={slideNext}
      >
        {customNextButton || "Next Item"}
      </div>
    </div>
  );
});

export default FHSlider;

/**
 * This component should only be used inside a FHSlider component.
 */
export function FHTab({ children, className = "" }) {
  const fhTabRef = useRef(null);

  function centerElement() {
    const sliderRef = document.getElementById("fhSliderRef");
    if (!sliderRef || !fhTabRef.current) return;
    const { clientWidth, scrollLeft } = sliderRef;
    const { offsetLeft, offsetWidth } = fhTabRef.current;

    if (offsetLeft < scrollLeft) {
      sliderRef.scrollTo({
        left: offsetLeft - clientWidth / 2 + offsetWidth / 2,
      });
    } else if (offsetLeft + offsetWidth > scrollLeft + clientWidth) {
      sliderRef.scrollTo({
        left:
          offsetLeft +
          offsetWidth -
          clientWidth +
          clientWidth / 2 -
          offsetWidth / 2,
      });
    }
  }

  return (
    <div
      className={`fh-tab__container ${className}`}
      onClick={centerElement}
      ref={fhTabRef}
    >
      {children}
    </div>
  );
}
